/**
 * Calendar Page
 * Monthly/Weekly/Daily views aggregating events from multiple entities
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';

interface CalendarEvent {
  eventId: string;
  eventType: string;
  eventDate: string;
  title: string;
  description: string | null;
  entityId: string;
  entityType: string;
  status: string;
  priority: string;
  accountName: string | null;
}

type ViewMode = 'month' | 'week' | 'day';

const EVENT_TYPE_COLORS: Record<string, string> = {
  task: 'bg-blue-100 text-blue-800 border-blue-300',
  invoice_due: 'bg-red-100 text-red-800 border-red-300',
  contract_end: 'bg-amber-100 text-amber-800 border-amber-300',
  subscription_renewal: 'bg-purple-100 text-purple-800 border-purple-300',
  recurring_billing: 'bg-green-100 text-green-800 border-green-300',
  activity: 'bg-cyan-100 text-cyan-800 border-cyan-300',
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  task: 'Task',
  invoice_due: 'Invoice Due',
  contract_end: 'Contract End',
  subscription_renewal: 'Subscription Renewal',
  recurring_billing: 'Recurring Billing',
  activity: 'Activity',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatDate(d: Date) {
  return d.toISOString().split('T')[0];
}

function addDays(d: Date, days: number) {
  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return result;
}

function startOfWeek(d: Date) {
  const result = new Date(d);
  result.setDate(result.getDate() - result.getDay());
  return result;
}

export function CalendarPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  // Calculate date range for query
  const dateRange = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (viewMode === 'month') {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      // Extend to cover visible calendar grid
      const start = startOfWeek(firstDay);
      const end = addDays(lastDay, 6 - lastDay.getDay());
      return { start: formatDate(start), end: formatDate(end) };
    } else if (viewMode === 'week') {
      const start = startOfWeek(currentDate);
      const end = addDays(start, 6);
      return { start: formatDate(start), end: formatDate(end) };
    } else {
      return { start: formatDate(currentDate), end: formatDate(currentDate) };
    }
  }, [currentDate, viewMode]);

  // Fetch events
  const { data: eventsData } = useQuery({
    queryKey: ['calendarEvents', dateRange.start, dateRange.end, eventTypeFilter],
    queryFn: () =>
      api.getCalendarEvents({
        startDate: dateRange.start,
        endDate: dateRange.end,
        eventType: eventTypeFilter !== 'all' ? eventTypeFilter : undefined,
      }),
  });
  const events = (eventsData?.data || []) as CalendarEvent[];

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((e) => {
      const dateKey = new Date(e.eventDate).toISOString().split('T')[0];
      const list = map.get(dateKey) || [];
      list.push(e);
      map.set(dateKey, list);
    });
    return map;
  }, [events]);

  // Navigation
  function navigate(direction: number) {
    const d = new Date(currentDate);
    if (viewMode === 'month') d.setMonth(d.getMonth() + direction);
    else if (viewMode === 'week') d.setDate(d.getDate() + direction * 7);
    else d.setDate(d.getDate() + direction);
    setCurrentDate(d);
  }

  function goToday() {
    setCurrentDate(new Date());
  }

  // Title
  const title = useMemo(() => {
    if (viewMode === 'month')
      return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate);
      const end = addDays(start, 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [currentDate, viewMode]);

  // Generate calendar grid for month view
  const monthGrid = useMemo(() => {
    if (viewMode !== 'month') return [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const start = startOfWeek(firstDay);
    const weeks: Date[][] = [];
    let current = new Date(start);
    for (let w = 0; w < 6; w++) {
      const week: Date[] = [];
      for (let d = 0; d < 7; d++) {
        week.push(new Date(current));
        current = addDays(current, 1);
      }
      weeks.push(week);
      // Stop if we've passed the month
      if (current.getMonth() > month && current.getFullYear() >= year && w >= 3) break;
    }
    return weeks;
  }, [currentDate, viewMode]);

  // Week days for week view
  const weekDays = useMemo(() => {
    if (viewMode !== 'week') return [];
    const start = startOfWeek(currentDate);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate, viewMode]);

  const today = formatDate(new Date());

  function renderEventBadge(event: CalendarEvent, compact = false) {
    const colorClass = EVENT_TYPE_COLORS[event.eventType] || 'bg-gray-100 text-gray-800 border-gray-300';
    return (
      <button
        key={event.eventId}
        className={`${colorClass} border rounded px-1 py-0.5 text-xs truncate w-full text-left cursor-pointer hover:opacity-80 transition-opacity ${compact ? 'text-[10px]' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedEvent(event);
        }}
      >
        {compact ? event.title.substring(0, 15) : event.title}
      </button>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-2xl font-bold">Calendar</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All Events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center border rounded-md">
            {(['month', 'week', 'day'] as ViewMode[]).map((mode) => (
              <Button
                key={mode}
                variant={viewMode === mode ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none first:rounded-l-md last:rounded-r-md"
                onClick={() => setViewMode(mode)}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold ml-2">{title}</h2>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${EVENT_TYPE_COLORS[key]?.split(' ')[0]}`} />
              <span className="text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Month View */}
      {viewMode === 'month' && (
        <div className="border rounded-lg overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b bg-muted/50">
            {DAYS.map((day) => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
          </div>
          {/* Calendar grid */}
          {monthGrid.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b last:border-b-0">
              {week.map((day) => {
                const dateKey = formatDate(day);
                const dayEvents = eventsByDate.get(dateKey) || [];
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const isToday = dateKey === today;

                return (
                  <div
                    key={dateKey}
                    className={`min-h-[100px] p-1 border-r last:border-r-0 ${
                      !isCurrentMonth ? 'bg-muted/30' : ''
                    }`}
                  >
                    <div
                      className={`text-sm mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                        isToday
                          ? 'bg-primary text-primary-foreground font-bold'
                          : isCurrentMonth
                            ? 'text-foreground'
                            : 'text-muted-foreground'
                      }`}
                    >
                      {day.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((event) => renderEventBadge(event, true))}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-muted-foreground text-center">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Week View */}
      {viewMode === 'week' && (
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-7">
            {weekDays.map((day) => {
              const dateKey = formatDate(day);
              const dayEvents = eventsByDate.get(dateKey) || [];
              const isToday = dateKey === today;

              return (
                <div key={dateKey} className="border-r last:border-r-0">
                  <div
                    className={`p-2 text-center border-b ${isToday ? 'bg-primary/10' : 'bg-muted/50'}`}
                  >
                    <div className="text-xs text-muted-foreground">{DAYS[day.getDay()]}</div>
                    <div
                      className={`text-lg font-semibold ${
                        isToday ? 'text-primary' : ''
                      }`}
                    >
                      {day.getDate()}
                    </div>
                  </div>
                  <div className="p-1 min-h-[400px] space-y-1">
                    {dayEvents.map((event) => renderEventBadge(event))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day View */}
      {viewMode === 'day' && (
        <div className="border rounded-lg p-4">
          {(() => {
            const dateKey = formatDate(currentDate);
            const dayEvents = eventsByDate.get(dateKey) || [];
            return (
              <div className="space-y-3">
                {dayEvents.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    No events for this day.
                  </div>
                )}
                {dayEvents.map((event) => {
                  const colorClass =
                    EVENT_TYPE_COLORS[event.eventType] || 'bg-gray-100 text-gray-800';
                  return (
                    <button
                      key={event.eventId}
                      className={`${colorClass} border rounded-lg p-4 w-full text-left cursor-pointer hover:opacity-90 transition-opacity`}
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium">{event.title}</h3>
                          {event.description && (
                            <p className="text-sm mt-1 opacity-80">{event.description}</p>
                          )}
                        </div>
                        <Badge variant="outline">
                          {EVENT_TYPE_LABELS[event.eventType] || event.eventType}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs opacity-70">
                        <span>{new Date(event.eventDate).toLocaleTimeString()}</span>
                        {event.accountName && <span>{event.accountName}</span>}
                        <span>Status: {event.status}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Event Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedEvent?.title}</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p>
                    <Badge className={EVENT_TYPE_COLORS[selectedEvent.eventType]}>
                      {EVENT_TYPE_LABELS[selectedEvent.eventType] || selectedEvent.eventType}
                    </Badge>
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date</Label>
                  <p>{new Date(selectedEvent.eventDate).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p>{selectedEvent.status}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Priority</Label>
                  <p>{selectedEvent.priority}</p>
                </div>
                {selectedEvent.accountName && (
                  <div>
                    <Label className="text-muted-foreground">Account</Label>
                    <p>{selectedEvent.accountName}</p>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">Entity</Label>
                  <p className="capitalize">{selectedEvent.entityType}</p>
                </div>
              </div>
              {selectedEvent.description && (
                <div>
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="text-sm mt-1">{selectedEvent.description}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Label({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`text-xs font-medium mb-0.5 ${className}`}>{children}</div>;
}
